import { Injectable, signal } from "@angular/core";
import { PermissionNames, PermissionStatus } from "../models/permissions.model";
import { TextControlService } from "./text-control.service";
import { PageInformation } from "./text-control.service.interface";
import { smartenQuotes } from "../helpers/smart-quote-helper";

declare const TXTextControl: any;

@Injectable()
export class ClipboardService {
  permission = signal<string>(PermissionStatus.Denied);

  constructor(private textControlService: TextControlService) {
    this.watchClipboardPermission();
  }

  public async getClipboardPermission() {
    return await navigator.permissions.query({
      name: PermissionNames.ClipboardRead,
    } as unknown as PermissionDescriptor);
  }

  private async watchClipboardPermission() {
    try {
      const permission = await this.getClipboardPermission();

      this.permission.set(permission.state);

      permission.onchange = () => {
        this.permission.set(permission.state);
      };
    } catch {
      this.permission.set(PermissionStatus.Denied);
    }
  }

  public async getClipboardItemAsHtml() {
    const clipboardItem = await this.getClipboardItem();

    if (!clipboardItem || clipboardItem.types == null || clipboardItem.types.length === 0) {
      return null;
    }

    let html: string = '';
    for (const type of clipboardItem.types) {
      if (type.startsWith('image/')) {
        const blob = await clipboardItem.getType(type);
        const dataUrl = await this.blobToDataURL(blob);
        const { width, height } = await this.getImageSize(dataUrl);
        // scale down image if needed
        const pageInformation = await this.textControlService.getPageInformationPromise();
        const scaledSize = this.calculateScaledImageSizeInPixels(pageInformation, width, height);

        return `<img src="${dataUrl}" alt="Pasted Image" width="${scaledSize.widthPx}" height="${scaledSize.heightPx}">`;
      } else if (type === 'text/html') {
        const blob = await clipboardItem.getType(type);
        const text = await blob.text();
        html = text;
      }
    }

    return html;
  }

  public async createClipboardPasteEvent(clipboardItem: ClipboardItem | null, plaintext: boolean = false) {
    if (clipboardItem == null) {
      return;
    }

    const dataTransfer = new DataTransfer();

    for (const type of clipboardItem.types) {
      const blob = await clipboardItem.getType(type);
      if (type.startsWith('image/')) {
        // For images, add them as files to DataTransfer
        const file = new File([blob], 'pasted-image.png', { type });
        dataTransfer.items.add(file);
      } else {
        // For text formats, use setData
        // if we want to paste plain text (no formatting), skip on the other text formats
        if (plaintext === true && type !== 'text/plain') {
          continue;
        }
        let text = await blob.text();
        text = smartenQuotes(text);
        dataTransfer.setData(type, text);
      }
    }

    const pasteEvent = new ClipboardEvent('paste', {
      clipboardData: dataTransfer,
      bubbles: true,
      cancelable: true,
    });

    const targetElement = document.getElementById('txHiddenTextField_textView');
    if (targetElement) {
      targetElement.dispatchEvent(pasteEvent);
    }
  }

  public async customPaste(html: string) {
    const fontName = await this.textControlService.getSelectionFontName();
    const fontSize = await this.textControlService.getSelectionFontSize();
    const body = new DOMParser().parseFromString(html, 'text/html').body as HTMLElement;
    // Smart quotes in text nodes (safe: doesn't touch attributes or markup)
    this.smartenTextNodes(body);
    await this.textControlService.callAsPromise(TXTextControl.beginUndoAction, 'Paste');

    try {
      const firstChild = body.children[0] as HTMLElement;
      if (firstChild) {
        const tagName = firstChild.tagName.toLowerCase();
        let firstChildHtml = '';
        if (tagName === 'table' || tagName === 'img') {
          firstChildHtml = firstChild.outerHTML;
        } else if (tagName === 'ol' || tagName === 'ul') {
          // insert new line
          const base64 = this.bytesToBase64(new TextEncoder().encode('\n'));
          await this.textControlService.loadIntoSelection(base64, TXTextControl.StreamType.PlainText, {});
          // insert the whole element
          firstChildHtml = firstChild.outerHTML;
        } else if (tagName !== 'p') {
          this.resetAllInlineStyles(firstChild, fontName, fontSize);
          // insert only what's inside the element
          firstChildHtml = firstChild.outerHTML;
        } else {
          this.resetAllInlineStyles(firstChild, fontName, fontSize);
          // insert only what's inside the element
          firstChildHtml = firstChild.innerHTML;
        }
        const base64 = this.bytesToBase64(new TextEncoder().encode(firstChildHtml));
        await this.textControlService.loadIntoSelection(base64, TXTextControl.StreamType.HTMLFormat, {});
      } else {
        const base64 = this.bytesToBase64(new TextEncoder().encode(body.innerHTML));
        await this.textControlService.loadIntoSelection(base64, TXTextControl.StreamType.HTMLFormat, {});

        return;
      }

      for (let i = 1; i < body.childNodes.length; i++) {
        const child = body.childNodes.item(i) as HTMLElement;

        if (!child) {
          continue;
        }

        let childHtml = '';
        if (child.tagName?.toLowerCase() === 'table') {
          childHtml = child.outerHTML;
          const base64 = this.bytesToBase64(new TextEncoder().encode(childHtml));
          await this.textControlService.loadIntoSelection(base64, TXTextControl.StreamType.HTMLFormat, {});
        } else if (child.innerHTML) {
          this.resetAllInlineStyles(child, fontName, fontSize);
          childHtml = child.innerHTML;
          const base64 = this.bytesToBase64(new TextEncoder().encode(childHtml));
          await this.textControlService.loadIntoSelection(base64, TXTextControl.StreamType.HTMLFormat, {});
        } else if (child.textContent) {
          // text nodes between elements are usually with value '\n\n', which would insert double new line
          // we want just to insert one new line
          const base64 = this.bytesToBase64(new TextEncoder().encode('\n'));
          await this.textControlService.loadIntoSelection(base64, TXTextControl.StreamType.PlainText, {});
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      await this.textControlService.callAsPromiseNoParams(TXTextControl.endUndoAction);
    }
  }

  public async getClipboardItem() {
    const clipboardItems = await navigator.clipboard.read();

    if (clipboardItems.length === 0) {
      return null;
    }

    const latestItem = clipboardItems[0]; // Get the most recent clipboard item

    return latestItem;
  }

  private bytesToBase64(bytes: any) {
    const binString = Array.from(bytes, (byte: any) => String.fromCodePoint(byte)).join('');
    return btoa(binString);
  }

  private resetAllInlineStyles(element: HTMLElement, fontName: string, fontSize: number) {
    const allowedStyles = ['font-weight', 'font-style', 'text-decoration'];

    if (element.hasAttribute('style')) {
      const style = element.getAttribute('style')!;
      const styleMap = new Map<string, string>();

      style.split(';').forEach((rule) => {
        const [prop, value] = rule.split(':').map((s) => s.trim());
        if (prop && value && allowedStyles.includes(prop)) {
          styleMap.set(prop, value);
        }
      });

      if (styleMap.size > 0) {
        const newStyle = Array.from(styleMap.entries())
          .map(([prop, value]) => `${prop}: ${value}`)
          .join('; ');
        element.setAttribute('style', newStyle);
      } else {
        element.removeAttribute('style');
      }
    }

    element.style.fontFamily = `'${fontName}'`;
    element.style.fontSize = `${fontSize}pt`;
    for (let i = 0; i < element.children.length; i++) {
      const child = element.children.item(i) as HTMLElement;
      if (child) {
        this.resetAllInlineStyles(child, fontName, fontSize);
      }
    }
  }

  private blobToDataURL(blob: Blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private getImageSize(dataUrl: any): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  private calculateScaledImageSizeInPixels(
    pageInformation: PageInformation,
    imageWidthPx: number,
    imageHeightPx: number,
    imageDPI: number = 96 // typically 96
  ): { widthPx: number; heightPx: number } {
    // TXTextControl gives us the page measurements in centiinch
    const maxWidthPx = this.convertToPixels(
      pageInformation.pageWidth - pageInformation.marginLeft - pageInformation.marginRight
    );
    const maxHeightPx = this.convertToPixels(
      pageInformation.pageHeight - pageInformation.marginTop - pageInformation.marginBottom
    );

    // Calculate scale factor
    const widthRatio = maxWidthPx / imageWidthPx;
    const heightRatio = maxHeightPx / imageHeightPx;

    const scale = Math.min(widthRatio, heightRatio, 1); // scale only if too big

    return {
      widthPx: Math.round(imageWidthPx * scale),
      heightPx: Math.round(imageHeightPx * scale),
    };
  }

  private convertToPixels(centiinchValue: number, imageDPI: number = 96) {
    // Convert centiinch to inches, then inches to pixels
    return (centiinchValue / 100) * imageDPI;
  }

  private smartenTextNodes(root: HTMLElement) {
    const SKIP_TAGS = new Set(['script', 'style', 'code', 'pre', 'textarea']);

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !node.parentElement) return NodeFilter.FILTER_REJECT;
        const tag = node.parentElement.tagName.toLowerCase();
        if (SKIP_TAGS.has(tag)) return NodeFilter.FILTER_REJECT;
        if (!/[\"']/g.test(node.nodeValue)) return NodeFilter.FILTER_REJECT; // no quotes => skip
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    let node: Node | null = walker.nextNode();
    while (node) {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue) {
        node.nodeValue = smartenQuotes(node.nodeValue);
      }
      node = walker.nextNode();
    }
  }
}
